/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from "@angular/common";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { Subject, takeUntil } from "rxjs";
import { ButtonModule } from "primeng/button";
import { ChipModule } from "primeng/chip";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { SkeletonModule } from "primeng/skeleton";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";

import { QPilotService } from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { TimezoneHelper } from "../../../../core/utils/timezone.helper";
import {
  ProcessingCycle,
  ScheduledOrderCyclesDialogData,
} from "../../interfaces/processing-cycle.interface";

/**
 * Scheduled Order Cycles Dialog Component
 * Shows the processing cycles history for a scheduled order (matches old QMC ScheduledOrderCyclesDialogComponent)
 */
@Component({
  selector: "app-scheduled-order-cycles-dialog",
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    ChipModule,
    SkeletonModule,
    TagModule,
    TooltipModule,
  ],
  templateUrl: "./scheduled-order-cycles-dialog.component.html",
  styleUrl: "./scheduled-order-cycles-dialog.component.scss",
})
export class ScheduledOrderCyclesDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  cycles: ProcessingCycle[] = [];
  isLoading = false;
  siteUtcOffset = 0;
  statusFilter: "all" | "success" | "failed" | "processing" | "queued" = "all";
  embeddedMode = false;

  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private timezoneHelper: TimezoneHelper,
    private router: Router,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<ScheduledOrderCyclesDialogData>,
  ) {}

  ngOnInit(): void {
    // Check if opened in embedded mode
    this.embeddedMode = this.config.data?.embeddedMode ?? false;
    console.log(
      "ScheduledOrderCyclesDialog - embeddedMode:",
      this.embeddedMode,
    );

    this.loadCycles();
  }

  /**
   * Get access token from config or session
   */
  private getAccessToken() {
    return this.config.data?.accessToken ?? this.sessionService.accessToken;
  }

  /**
   * Load scheduled order processing cycles
   */
  private loadCycles(): void {
    const accessToken = this.getAccessToken();
    if (
      !this.config.data?.scheduledOrderId ||
      !this.config.data?.siteId ||
      !accessToken
    ) {
      return;
    }

    this.isLoading = true;

    // Load site data for timezone calculations
    this.qpilotService
      .getSite(this.config.data.siteId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (site) => {
          this.siteUtcOffset = (site as any).UtcOffset ?? 0;
        },
        error: (error) => {
          console.error("Failed to load site data:", error);
        },
      });

    // Load processing cycles
    this.qpilotService
      .getScheduledOrderProcessingCycles(
        accessToken,
        this.config.data.scheduledOrderId,
        this.config.data.siteId,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          // Sort by CreatedUtc descending (most recent first)
          this.cycles = result.Items.map((cycle) => ({
            ...cycle,
            CreatedUtc: new Date(cycle.CreatedUtc),
            StartDateUtc: cycle.StartDateUtc
              ? new Date(cycle.StartDateUtc)
              : null,
            EndDateUtc: cycle.EndDateUtc ? new Date(cycle.EndDateUtc) : null,
          })).sort((a, b) => b.CreatedUtc.getTime() - a.CreatedUtc.getTime());

          this.isLoading = false;
        },
        error: (error) => {
          console.error("Failed to load processing cycles:", error);
          this.isLoading = false;
        },
      });
  }

  /**
   * Get filtered cycles based on status filter
   */
  get filteredCycles(): ProcessingCycle[] {
    if (this.statusFilter === "all") {
      return this.cycles;
    }
    return this.cycles.filter((cycle) => {
      const status = cycle.Status?.toLowerCase();
      return status === this.statusFilter;
    });
  }

  /**
   * Toggle status filter
   */
  toggleFilter(
    filter: "all" | "success" | "failed" | "processing" | "queued",
  ): void {
    // If clicking the same filter, reset to 'all'
    if (this.statusFilter === filter) {
      this.statusFilter = "all";
    } else {
      this.statusFilter = filter;
    }
  }

  /**
   * Get status statistics for display
   */
  getStatusStats(): {
    total: number;
    success: number;
    failed: number;
    processing: number;
    queued: number;
  } {
    const total = this.cycles.length;
    const success = this.cycles.filter(
      (c) => c.Status?.toLowerCase() === "success",
    ).length;
    const failed = this.cycles.filter(
      (c) => c.Status?.toLowerCase() === "failed",
    ).length;
    const processing = this.cycles.filter(
      (c) => c.Status?.toLowerCase() === "processing",
    ).length;
    const queued = this.cycles.filter(
      (c) => c.Status?.toLowerCase() === "queued",
    ).length;

    return { total, success, failed, processing, queued };
  }

  /**
   * Get the severity for cycle status display
   */
  getCycleSeverity(status: string): "success" | "info" | "warning" | "danger" {
    switch (status?.toLowerCase()) {
      case "success":
        return "success";
      case "failed":
        return "danger";
      case "processing":
        return "info";
      case "queued":
        return "info";
      default:
        return "info";
    }
  }

  /**
   * Format the date for display with site timezone
   */
  formatDate(date: Date | null): string {
    if (!date) return "-";
    return this.timezoneHelper.formatDateWithTimezone(date, this.siteUtcOffset);
  }

  /**
   * Format currency values
   */
  formatCurrency(value: number | null | undefined): string {
    if (value == null || value === 0) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  }

  /**
   * Navigate to processing cycle details view (dual mode: embedded vs standard)
   */
  goToProcessingCycleView(processingCycleId: number): void {
    if (this.embeddedMode) {
      // Embedded mode: close dialog and return navigation data
      // Parent component will handle the navigation
      console.log(
        "Dialog (embedded mode) - closing with navigation data for cycle:",
        processingCycleId,
      );
      this.ref.close({
        action: "navigate-to-cycle",
        processingCycleId: processingCycleId,
        scheduledOrderId: this.config.data!.scheduledOrderId,
        siteId: this.config.data!.siteId,
      });
    } else {
      // Standard mode: navigate directly using Angular Router
      this.ref.close();

      // Check if we're in widgets route or regular dashboard route
      const currentUrl = this.router.url;
      let navigationUrl: string;

      if (currentUrl.includes("/widgets/")) {
        navigationUrl = `/widgets/dashboard/${this.config.data!.siteId}/scheduled-orders/${this.config.data!.scheduledOrderId}/edit/processing-cycles/${processingCycleId}`;
      } else {
        navigationUrl = `/dashboard/${this.config.data!.siteId}/scheduled-orders/${this.config.data!.scheduledOrderId}/edit/processing-cycles/${processingCycleId}`;
      }

      console.log("Dialog (standard mode) - navigating to:", navigationUrl);
      void this.router.navigateByUrl(navigationUrl);
    }
  }

  /**
   * Close the dialog
   */
  onClose(): void {
    this.ref.close();
  }

  /**
   * Track by function for ngFor performance
   */
  trackByCycleId(index: number, cycle: ProcessingCycle): number {
    return cycle.Id;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
